import { useState, useEffect } from 'react';
import AgGrid, { StarIcon, ClickableCell, StatusCell } from '../common/AgGrid';
import { useNavigate } from 'react-router-dom';
import { GetClientPortal, MarkFavorite } from '../../services/accountService';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import { isRestrictedUser } from '../../utils/PermissionUtils';
const Dashboard = () => {
  const navigate = useNavigate();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingFavorite, setUpdatingFavorite] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [searchText, setSearchText] = useState('');
  const isConsultantOrClient = isRestrictedUser();
 
  const [favoritesState, setFavoritesState] = useState(() => {
    
    const savedFavorites = localStorage.getItem('clientFavorites');
    return savedFavorites ? JSON.parse(savedFavorites) : {};
  });

 
  useEffect(() => {
    sessionStorage.removeItem('jobDetailsSource');
    sessionStorage.removeItem('isAllClientsFlow');
  }, []);

  
  const sortDataWithFavoritesOnTop = (data) => {
    return [...data].sort((a, b) => {
    
      if (a.favorite !== b.favorite) {
        return a.favorite ? -1 : 1;
      }
     
      return (a.clientName || '').localeCompare(b.clientName || '');
    });
  };

 
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };


  useEffect(() => {
    localStorage.setItem('clientFavorites', JSON.stringify(favoritesState));
  }, [favoritesState]);

 
  useEffect(() => {
    fetchClientPortals();
  }, []);

 
  useEffect(() => {
    if (rowData.length > 0) {
      setRowData(prevData => sortDataWithFavoritesOnTop(prevData));
    }
  }, [favoritesState]);

  const fetchClientPortals = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await GetClientPortal();

      if (!response.isSuccess) {
        throw new Error(response.error || 'Failed to fetch client data');
      }

    
      const formattedData = response.data.map(item => ({
        Id: item.clientId,
        clientId: item.clientId,
        clientCode: item.clientCode,
        clientName: item.clientName || '',
        lastUpdate: formatDate(item.lastUpdate),
        consultantName: item.consultantName || 'Not Assigned',
        status: item.status ? 'Active' : 'Inactive',
        
        favorite: favoritesState[item.clientId]?.isFavorite ?? (item.favorite || false),
        
        favoriteInfo: favoritesState[item.clientId] || {
          isFavorite: item.favorite || false,
          clientName: item.clientName || '',
          updatedAt: new Date().toISOString()
        }
      }));

    
      const sortedData = sortDataWithFavoritesOnTop(formattedData);
      setRowData(sortedData);
    } catch (err) {
      console.error('Error fetching client portals:', err);
      setError(err.message || 'Failed to load client data');
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return '';
    }
  };

 
  const handleFavoriteToggle = async (clientId, currentFavoriteStatus, clientName) => {
    if (isConsultantOrClient) return;
   
    if (updatingFavorite === clientId) return;

    const originalFavoriteStatus = currentFavoriteStatus;

    try {
      setUpdatingFavorite(clientId);

      const newFavoriteStatus = !currentFavoriteStatus;
      const favoriteInfo = {
        isFavorite: newFavoriteStatus,
        clientName: clientName,
        clientId: clientId,
        updatedAt: new Date().toISOString()
      };

      
      setFavoritesState(prev => ({
        ...prev,
        [clientId]: favoriteInfo
      }));

     
      setRowData(prevData => {
        const updatedData = prevData.map(item =>
          item.clientId === clientId
            ? {
              ...item,
              favorite: newFavoriteStatus,
              favoriteInfo: favoriteInfo
            }
            : item
        );
       
        return sortDataWithFavoritesOnTop(updatedData);
      });

    
      await MarkFavorite(clientId, newFavoriteStatus);

      
      showNotification(
        `${clientName} ${newFavoriteStatus ? 'added to' : 'removed from'} favorites`,
        'success'
      );

    } catch (err) {
      console.error('Error in handleFavoriteToggle:', err);

   
      setFavoritesState(prev => {
        const updated = { ...prev };
        if (originalFavoriteStatus) {
          updated[clientId] = {
            isFavorite: originalFavoriteStatus,
            clientName: clientName,
            clientId: clientId,
            updatedAt: new Date().toISOString()
          };
        } else {
          delete updated[clientId];
        }
        return updated;
      });

     
      setRowData(prevData => {
        const revertedData = prevData.map(item =>
          item.clientId === clientId
            ? {
              ...item,
              favorite: originalFavoriteStatus,
              favoriteInfo: {
                isFavorite: originalFavoriteStatus,
                clientName: clientName,
                updatedAt: new Date().toISOString()
              }
            }
            : item
        );
        return sortDataWithFavoritesOnTop(revertedData);
      });

      
      showNotification(
        `Failed to update favorite: ${err.message || 'Please try again'}`,
        'error'
      );
    } finally {
      setUpdatingFavorite(null);
    }
  };

 
  const getAllFavorites = () => {
    return Object.entries(favoritesState)
      .filter(([_, data]) => data.isFavorite)
      .map(([clientId, data]) => ({
        clientId,
        ...data
      }));
  };

 
  const getFavoriteInfo = (clientId) => {
    return favoritesState[clientId] || null;
  };

 
  const CustomStarIcon = ({ isFavorite, isLoading }) => {
    return (
      <span
        style={{
          fontSize: '20px',
          color: isLoading ? '#ccc' : (isFavorite ? '#FFD700' : '#ccc'),
          display: 'inline-block',
          transition: 'color 0.3s ease'
        }}
        title={isLoading ? 'Updating...' : (isFavorite ? 'Remove from favorites' : 'Add to favorites')}
      >
        {isFavorite ? '★' : '☆'}
      </span>
    );
  };

 
  const allColumnDefs = [
    {
      field: 'Id',
      headerName: 'Client ID',
      filter: 'agTextColumnFilter',
      sortable: true,
      minWidth: 150,
      flex: 1,
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'clientCode',
      headerName: 'Client Code',
      filter: 'agTextColumnFilter',
      sortable: true,
      minWidth: 150,
      flex: 1,
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'clientName',
      headerName: 'Client Name',
      filter: 'agTextColumnFilter',
      sortable: true,
      width: 300,
      minWidth: 300,
      maxWidth: 300,
      cellRenderer: (params) => {
       
        if (!params.data) {
          return params.value || '';
        }

        const { clientId, clientName } = params.data;
        return (
          <ClickableCell
            value={clientName}
            onClick={() => {
             
              sessionStorage.setItem('jobDetailsSource', 'client-dashboard');
              sessionStorage.setItem('isAllClientsFlow', 'false');

              navigate('/client-dashboard', {
                state: {
                  clientId: clientId,
                  clientName: clientName,
                  favoriteInfo: params.data.favoriteInfo,
                  allFavorites: getAllFavorites(),
                  favoritesState: favoritesState,
                 
                  source: 'client-dashboard',
                  isAllClients: false
                }
              });
            }}
            style={{
              color: '#007bff',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontWeight: '500'
            }}
          />
        );
      }
    },
    {
      field: 'lastUpdate',
      headerName: 'Last Update',
      filter: 'agTextColumnFilter',
      sortable: true,
      width: 180,
      maxWidth: 180,
      minWidth: 180,
      comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'status',
      headerName: 'Status',
      filter: 'agTextColumnFilter',
      sortable: true,
      width: 180,
      maxWidth: 180,
      minWidth: 180,
      cellRenderer: (params) => {
        
        if (!params.data) {
          return params.value || '';
        }
        return <StatusCell value={params.value} />;
      },
      cellStyle: (params) => {
        
        if (!params.data) return { textAlign: 'center' };
        return {
          color: params.value === 'Active' ? 'green' : 'red',
          fontWeight: 'bold',
          textAlign: 'center'
        };
      }
    },
    {
      field: 'favorite',
      headerName: 'Favorite',
      filter: false,
      sortable: true,
      width: 180,
      maxWidth: 180,
      minWidth: 180,
      cellStyle: { textAlign: 'center' },
      comparator: (valueA, valueB) => valueA === valueB ? 0 : valueA ? -1 : 1,
      cellRenderer: (params) => {
       
        if (!params.data) {
          return '';
        }

        const { clientId, clientName, favorite } = params.data;
        const isUpdating = updatingFavorite === clientId;

        return (
          <div
            onClick={(e) => {
              if (!isUpdating) {
                e.stopPropagation();
                handleFavoriteToggle(clientId, favorite, clientName);
              }
            }}
            style={{
              cursor: isUpdating ? 'not-allowed' : 'pointer',
              display: 'inline-block',
              padding: '5px',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => {
              if (!isUpdating) e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              if (!isUpdating) e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <CustomStarIcon
              isFavorite={favorite}
              isLoading={isUpdating}
            />
          </div>
        );
      },
      comparator: (valueA, valueB) => valueA === valueB ? 0 : valueA ? -1 : 1
    }
  ];

  const columnDefs = isConsultantOrClient
    ? allColumnDefs.filter(col => col.field !== 'favorite')
    : allColumnDefs;
  
  const Notification = () => {
    if (!notification.show) return null;

    const backgroundColor = notification.type === 'error' ? '#F44336' : '#4CAF50';

    return (
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          borderRadius: '4px',
          color: 'white',
          fontWeight: '500',
          zIndex: '1000',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          backgroundColor,
          fontSize: '14px'
        }}
      >
        {notification.message}
      </div>
    );
  };

  
  const handleClearFavorites = () => {
    localStorage.removeItem('clientFavorites');
    setFavoritesState({});
    showNotification('All favorites cleared', 'success');
   
    fetchClientPortals();
  };

 
  if (loading) {
    return (
      <div className="dashboard-container">
        <Notification />
        <AisScreenLoader /> 
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-container">
        <Notification />
        <div className="content-section">
          <div className="section-header">
            <div className="header-left">
              <h1 className="section-title">Client Select</h1>
              <p className="section-subtitle">Please select a client from the list below.</p>
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '50px', color: 'gray' }}>
            <div>{rowData.length === 0 && <div>No Client Found</div>}</div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (rowData.length === 0 && !loading) {
    return (
      <div className="dashboard-container">
        <Notification />
        <div className="content-section">
          <div className="section-header">
            <div className="header-left">
              <h1 className="section-title">Client Select</h1>
              <p className="section-subtitle">Please select a client from the list below.</p>
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <div>No clients found.</div>
          </div>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="dashboard-container">
      <Notification />
      <div className="content-section">
        <div className="section-header">
          <div className="header-left">
            <h1 className="section-title">Client Select</h1>
            <p className="section-subtitle">Please select a client from the list below.</p>
          </div>
        </div>

        <AgGrid
          rowData={rowData}
          columnDefs={columnDefs}
          enableSearch={true}
          pagination={true}
          quickFilterText={searchText}
          onQuickFilterChange={setSearchText}
          paginationPageSize={10}
          hideExportButton={true}
          hideSideBar={true}
          hideSelectAll={true}
          searchPlaceholder="Search clients..."
          height="500px"
        />
      </div>
    </div>
  );
};

export default Dashboard;